import React, { Component, createRef, RefObject } from 'react';
import { Bus, WindowAdapter } from '@waves/waves-browser-bus';
// @ts-ignore
import queryString from 'query-string';
import {
  BusWebSocketEventMap,
  events, handlers,
  iframetoParentEvents, iframeToParentHandlers, wsTypes, wslistenerType,
} from './types';

const timeout = 30000;

// Methods
interface IJSFiller {
  openPdfOffline: handlers['openPdfOffline'],
  open: handlers['open'],
  openBySessionId: handlers['openBySessionId'],
  setMaxSessionsCount: handlers['setMaxSessionsCount'],
  getIsSessionExistByProjectId: handlers['getIsSessionExistByProjectId'],
  getSessionIdByProjectId: handlers['getSessionIdByProjectId'],
  getWebSocketProxy(): IWebSocketProxy,
}

export interface IWebSocketProxy {
  on<K extends wsTypes>(type: K, listener: wslistenerType<K>): void,
  off<K extends wsTypes>(type: K, listener: wslistenerType<K>): void,
  send<K extends wsTypes> (type: K, event: BusWebSocketEventMap[K]): void,
}

// Properties
interface IProps {
  url: string, // URL address to JSFiller (without query strings)
  debug: boolean, // Debug mode - turn on console.log messages
  offline: boolean // Offline mode (disabled websocket)

  // callback will be called after connecting to the JSFiller
  onReady: (
    props: events['ready']
  ) => void,

  // callback will be called after open project in JSFiller
  // (open, openPdfOffline, openBySessionId methods)
  onOpen: (
    props: events['open']
  ) => void,

  // callback will be called after destroy project
  // (done, destroy methods)
  onDestroy: (
    props: events['destroy']
  ) => void,

  // callback will be called after save project
  // (save method)
  onSave: (
    props: events['save']
  ) => void,

  // When JSFiller get internal destroy, JSFiller call this
  // handler, and resume destroy if get { isDestroyed: true }
  handleDestroy: iframeToParentHandlers['destroy'],
}

class WebSocketProxy implements IWebSocketProxy {
  bus: Bus<BusWebSocketEventMap>;

  constructor(bus: Bus<BusWebSocketEventMap>) {
    this.bus = bus;
  }

  on<K extends wsTypes>(type: K, listener: wslistenerType<K>): void {
    this.bus.on(type, listener);
  }

  off<K extends wsTypes>(type: K, listener: wslistenerType<K>): void {
    this.bus.off(type, listener);
  }

  send<K extends wsTypes>(type: K, event: BusWebSocketEventMap[K]): void {
    this.bus.dispatchEvent(type, event);
  }
}

class JSFiller extends Component<IProps> implements IJSFiller {
  static defaultProps = {
    debug: false,
  };

  constructor(props: IProps) {
    super(props);
    this.iframe = createRef();
  }

  componentDidMount() {
    if (this.iframe.current) {
      this.initialize(this.iframe.current).then(() => {
        this.log('initialized');
      });
    } else {
      this.log('something wrong with iframe');
    }
  }

  componentWillUnmount() {
    if (this.bus) {
      this.bus.off('ready', this.onReady);
      this.bus.off('open', this.onOpen);
      this.bus.off('save', this.onSave);
      this.bus.off('destroy', this.onDestroy);
    }
    if (this.reverseBus) {
      this.reverseBus.unregisterHandler('destroy');
    }
  }

  // eslint-disable-next-line react/sort-comp
  iframe: RefObject<HTMLIFrameElement>;

  // events from JSFiller, actions to JSFiller
  bus: Bus<events, handlers> | undefined;

  // events from parent window, actions from JSFiller
  reverseBus: Bus<iframetoParentEvents, iframeToParentHandlers> | undefined;

  // events from WebSocket
  // AB: надо както прокинуть наружу так или обернуть в типизированный клас и его прокинуть наружу
  wsBus: Bus<BusWebSocketEventMap> | undefined;

  log = (...message: any[]): void => {
    if (this.props.debug) {
      // eslint-disable-next-line no-console
      console.log('@@bus -', ...message);
    }
  }

  async initialize(iframe: HTMLIFrameElement) {
    const adapter = await WindowAdapter.createSimpleWindowAdapter(iframe);
    this.bus = new Bus<events, handlers>(adapter);
    this.reverseBus = (
      this.bus as unknown as Bus<iframetoParentEvents, iframeToParentHandlers>
    );
    this.wsBus = (
      this.bus as unknown as Bus<BusWebSocketEventMap>
    );

    this.bus.on('ready', this.onReady);
    this.bus.on('open', this.onOpen);
    this.bus.on('save', this.onSave);
    this.bus.on('destroy', this.onDestroy);
    this.reverseBus.registerRequestHandler('destroy', this.handleDestroy);
  }

  openPdfOffline: handlers['openPdfOffline'] = (data) => {
    if (this.bus) {
      return this.bus.request('openPdfOffline', data, timeout);
    }

    return Promise.reject(new Error('openPdfOffline, bus error'));
  }

  handleDestroy: iframeToParentHandlers['destroy'] = (data) => {
    return this.props.handleDestroy(data);
  }

  done: handlers['done'] = (data) => {
    if (this.bus) {
      return this.bus.request('done', data, timeout);
    }

    return Promise.reject(new Error('done, bus error'));
  }

  destroy: handlers['destroy'] = (data) => {
    if (this.bus) {
      return this.bus.request('destroy', data, timeout);
    }

    return Promise.reject(new Error('destroy, bus error'));
  }

  save: handlers['save'] = () => {
    if (this.bus) {
      return this.bus.request('save', undefined, timeout);
    }

    return Promise.reject(new Error('save, bus error'));
  }

  open: handlers['open'] = (data) => {
    if (this.bus) {
      return this.bus.request('open', data, timeout);
    }

    return Promise.reject(new Error('open, bus error'));
  }

  openBySessionId: handlers['openBySessionId'] = (data) => {
    if (this.bus) {
      return this.bus.request('openBySessionId', data, timeout);
    }

    return Promise.reject(new Error('openBySessionId, bus error'));
  }

  setMaxSessionsCount: handlers['setMaxSessionsCount'] = (data) => {
    if (this.bus) {
      return this.bus.request('setMaxSessionsCount', data, timeout);
    }

    return Promise.reject(new Error('setMaxSessionsCount, bus error'));
  }

  getIsSessionExistByProjectId: handlers['getIsSessionExistByProjectId'] = (data) => {
    if (this.bus) {
      return this.bus.request('getIsSessionExistByProjectId', data, timeout);
    }

    return Promise.reject(new Error('getIsSessionExistByProjectId, bus error'));
  }

  getSessionIdByProjectId: handlers['getSessionIdByProjectId'] = (data) => {
    if (this.bus) {
      return this.bus.request('getSessionIdByProjectId', data, timeout);
    }

    return Promise.reject(new Error('getSessionIdByProjectId, bus error'));
  }

  onReady = ({ message }: events['ready']) => {
    this.log(`onReady event received "${message}"`);
    this.props.onReady({ message });
  };

  onSave = ({ message }: events['save']) => {
    this.log(`onSave event received "${message}"`);
    this.props.onSave({ message });
  };

  onDestroy = ({ message }: events['destroy']) => {
    this.log(`onDestroy event received "${message}"`);
    this.props.onDestroy({ message });
  };

  onOpen = ({ sessionId }: events['open']) => {
    this.log(`onOpen event received sessionId: "${sessionId}"`);
    if (sessionId) { // we have sessionId only in online mode
      this.props.onOpen({ sessionId });
    } else {
      this.props.onOpen({});
    }
  }

  getWebSocketProxy(): IWebSocketProxy {
    return new WebSocketProxy(this.wsBus);
  }

  render() {
    const query = queryString.stringify({
      embedded: true,
      isOfflineMode: this.props.offline || undefined,
      token: 'token',
    });

    const { url } = this.props;
    return (
      <iframe
        src={`${url}?${query}`}
        title="JSFiller"
        ref={this.iframe}
      />
    );
  }
}

export default JSFiller;
